import React, { useState, useEffect } from "react";
import {
  EditOutlined,
  CopyOutlined,
  CheckOutlined
} from "@ant-design/icons";
import { Button, Modal, Avatar, Descriptions, message, Select } from "antd";
import moment from "moment-timezone";
import { Link } from "react-router-dom";
import FormatDate from "../FormatDate/FormatDate";
import { AppointmentStatus } from "../GlobalComponents/StatusLabels";
import { todayAppointmentActions } from "../../utils/Data";
import { doPatch } from "../../API/apis";
import { useStateGlobalContext } from "../../contexts/GlobalContext";
import Notification from "../Notification/Notification";
import PatientAppointmentsModal from "../PatientAppointmentsModal/PatientAppointmentsModal";
import "./ViewAppointmentModal.css";

const ViewAppointmentModal = ({
  isVisible,
  onClose,
  onEdit,
  appointmentData,
  officeTimezone,
  onStatusUpdate,
}) => {
  const [isCopied, setIsCopied] = useState(false);
  const [isContactCopied, setIsContactCopied] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [currentStatus, setCurrentStatus] = useState(null);
  const [upcomingApptsVisible, setUpcomingApptsVisible] = useState(false);
  const { getAppointments } = useStateGlobalContext();

  // Sync local status with appointmentData when it changes
  useEffect(() => {
    if (appointmentData?.current_status) {
      setCurrentStatus(appointmentData.current_status);
    }
  }, [appointmentData?.current_status]);

  if (!appointmentData) return null;

  const {
    first_name,
    middle_name,
    last_name,
    dob,
    gender,
    contact,
    service_name,
    appointment_with,
    reason_for_visit,
    appointment_date,
    duration,
    current_status,
    patient_id
  } = appointmentData;

  const patientName = `${first_name || ''} ${middle_name || ''} ${last_name || ''}`.trim();
  const appointmentDateTime = moment(appointment_date).tz(officeTimezone);
  const endTime = appointmentDateTime.clone().add(duration, 'minutes');

  const handleCopyDateTime = async () => {
    message.success("Copied to clipboard");
    const dateTimeString = `${appointmentDateTime.format('MMM DD, YYYY')}, ${appointmentDateTime.format('h:mm A')}`;
    try {
      await navigator.clipboard.writeText(dateTimeString);
      setIsCopied(true);
      setTimeout(() => {
        setIsCopied(false);
      }, 2000); // Reset after 2 seconds
    } catch (err) {
      message.error("Failed to copy to clipboard");
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = dateTimeString;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setIsCopied(true);
      setTimeout(() => {
        setIsCopied(false);
      }, 2000); // Reset after 2 seconds
    }
  };

  const handleCopyContact = async () => {
    message.success("Contact copied to clipboard");
    try {
      await navigator.clipboard.writeText(contact);
      setIsContactCopied(true);
      setTimeout(() => {
        setIsContactCopied(false);
      }, 2000); // Reset after 2 seconds
    } catch (err) {
      message.error("Failed to copy to clipboard");
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = contact;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setIsContactCopied(true);
      setTimeout(() => {
        setIsContactCopied(false);
      }, 2000); // Reset after 2 seconds
    }
  };

  const handleStatusChange = async (value) => {
    const statusMap = {
      1: "Scheduled",
      4: "Appt. Sent",
      8: "Confirmed",
      2: "Arrived",
      3: "Rescheduled",
      5: "Cancelled",
      6: "No Show"
    };

    const newStatus = statusMap[value];
    const data = { current_status: newStatus };

    setIsUpdatingStatus(true);
    try {
      const response = await doPatch(
        `/appointments/update/${appointmentData.id}/`,
        data
      );
      if (response.status === 200) {
        Notification.success("Status updated successfully");
        setCurrentStatus(newStatus); // Update local status immediately
        setSelectedStatus(null);
        // Refresh data from parent component (calendar or today's list)
        if (onStatusUpdate) {
          await onStatusUpdate();
        }
        // Also refresh global appointments if available
        if (getAppointments) {
          await getAppointments();
        }
      } else {
        Notification.error("Failed to update status");
      }
    } catch (error) {
      console.error("Error updating status:", error);
      Notification.error("An error occurred while updating status");
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  return (
    <Modal
      open={isVisible}
      onCancel={onClose}
      closeIcon={
        <Button size="small" className="app-close-icon" shape="round">
          Close
        </Button>
      }
      title="Appointment Details"
      footer={[
        <Button key="close" onClick={onClose}>
          Close
        </Button>,
        <Button key="edit" type="primary" icon={<EditOutlined />} onClick={onEdit}>
          Edit
        </Button>
      ]}
      width={500}
    >
      <div className="mb-4 flex justify-between items-center">
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
          <Avatar size={48} style={{ marginRight: 12 }}>
            {first_name?.[0]?.toUpperCase() || 'A'}
          </Avatar>
          <div>
            <div style={{ fontWeight: 'bold', fontSize: 16 }}>
              {patient_id ? (
                <Link
                  to={`/main/charts/patient/${patient_id}/`}
                  style={{ color: '#1890ff', cursor: 'pointer' }}
                  onClick={onClose}
                >
                  {patientName}
                </Link>
              ) : (
                patientName
              )}
            </div>
            {dob && (
              <div style={{ fontSize: 12, color: '#666' }}>
                DOB: <FormatDate date={dob} /> • {gender || 'Unknown'}
              </div>
            )}
          </div>
        </div>
        <Button
          type="primary"
          size="small"
          disabled={!patient_id}
          onClick={() => setUpcomingApptsVisible(true)}
        >
          Upcoming Appts
        </Button>
      </div>

      <PatientAppointmentsModal
        visible={upcomingApptsVisible}
        onClose={() => setUpcomingApptsVisible(false)}
        patientId={patient_id}
        patientName={patientName}
      />

      <Descriptions column={1} size="small" bordered>
        <Descriptions.Item label="Date">
          {appointmentDateTime.format('MMM DD, YYYY')}
        </Descriptions.Item>
        <Descriptions.Item label="Time">
          <div className="flex justify-between items-center">
            <span>{appointmentDateTime.format('h:mm A')} - {endTime.format('h:mm A')} ({duration} min)</span>
            {isCopied ? (
              <CheckOutlined
                title="Copied!"
                className="text-green-500 transition-all duration-300 ease-in-out"
                style={{
                  transform: 'scale(1.2)',
                  animation: 'pulse 0.6s ease-in-out'
                }}
              />
            ) : (
              <CopyOutlined
                title="Copy Appointment Date and Time"
                onClick={handleCopyDateTime}
                className="text-[#1890ff] hover:text-[#40a9ff] cursor-pointer transition-colors duration-200"
              />
            )}

          </div>
        </Descriptions.Item>
        <Descriptions.Item label="Provider">
          {appointment_with}
        </Descriptions.Item>
        {service_name && (
          <Descriptions.Item label="Service">
            {service_name}
          </Descriptions.Item>
        )}
        {contact && (
          <Descriptions.Item label="Contact">
            <div className="flex justify-between items-center">
              <span>{contact}</span>
              {isContactCopied ? (
                <CheckOutlined
                  title="Copied!"
                  className="text-green-500 transition-all duration-300 ease-in-out"
                  style={{
                    transform: 'scale(1.2)',
                    animation: 'pulse 0.6s ease-in-out'
                  }}
                />
              ) : (
                <CopyOutlined
                  title="Copy Contact"
                  onClick={handleCopyContact}
                  className="text-[#1890ff] hover:text-[#40a9ff] cursor-pointer transition-colors duration-200"
                />
              )}
            </div>
          </Descriptions.Item>
        )}
        {currentStatus && (
          <Descriptions.Item label="Current Status">
            <AppointmentStatus status={currentStatus} />
          </Descriptions.Item>
        )}
        <Descriptions.Item label="Change Status">
          <Select
            placeholder="Select a status to update"
            style={{ width: '100%' }}
            value={selectedStatus}
            onChange={handleStatusChange}
            loading={isUpdatingStatus}
            disabled={isUpdatingStatus}
            allowClear
            onClear={() => setSelectedStatus(null)}
          >
            {todayAppointmentActions
              .filter((status) => status.label.toLowerCase() !== "delete")
              .map((status) => (
                <Select.Option key={status.key} value={status.key}>
                  {status.label}
                </Select.Option>
              ))}
          </Select>
        </Descriptions.Item>
      </Descriptions>

      {reason_for_visit && (
        <div style={{ marginTop: 16 }}>
          <div style={{ fontWeight: 'bold', marginBottom: 8 }}>Reason for Visit:</div>
          <div style={{ padding: 8, backgroundColor: '#f5f5f5', borderRadius: 4 }}>
            {reason_for_visit}
          </div>
        </div>
      )}
    </Modal>
  );
};

export default ViewAppointmentModal;
